import { DistinctFilterMetadata, RangeFilterMetadata } from "creso-core/types";
import { useCallback, useMemo } from "react";
import { useFundFiltersMetadata } from "creso-core/hooks";
import { FilterConfig } from "@funds/config";
import { Switch } from "web-design-system";
import { MultiPicker } from "./multi-picker";
import { FilterSection } from "./filter-section";
import { RangePicker } from "./range-picker";

export function FilterRenderer({
  onFiltersChange,
  filters,
  filterConfig,
  filterSearch,
}: {
  filterSearch: string;
  onFiltersChange?: (filters: Record<string, string[] | string>) => void;
  filters: Record<string, string[] | string>;
  filterConfig: FilterConfig[];
}) {
  const visibleFilters = useMemo(
    () => filterConfig.filter((fc) => fc.visible),
    [filterConfig]
  );

  const visibleFiltersIds = useMemo(
    () => visibleFilters.map((fc) => fc.fieldId).filter(Boolean) as string[],
    [visibleFilters]
  );

  const { data: filtersMetadata } = useFundFiltersMetadata(visibleFiltersIds);

  const updateFilter = useCallback(
    (field: string, values: string[]) => {
      let previousFieldFilters = [...(filters[field] || [])];
      values.forEach((value) => {
        if (previousFieldFilters.includes(value)) {
          previousFieldFilters = previousFieldFilters.filter(
            (f) => f !== value
          );
        } else {
          previousFieldFilters.push(value);
        }
      });
      onFiltersChange?.({ ...filters, [field]: previousFieldFilters });
    },
    [filters, onFiltersChange]
  );

  const isSearching = filterSearch.trim().length > 0;

  return (
    <div className="flex flex-col">
      {visibleFilters.map((c, index) => {
        const defaultOpen = index === visibleFilters.length - 1;

        if (c.type === "MULTI") {
          const metadata = filtersMetadata?.find((f) => f.field === c.id);
          const values = (
            (metadata as DistinctFilterMetadata)?.values || []
          ).filter((v) => v.name.toLocaleLowerCase().includes(filterSearch));
          return (
            <FilterSection
              key={c.id}
              showEmpty={values.length === 0}
              title={c.label as string}
              open={isSearching ? true : undefined}
              defaultOpen={defaultOpen}
            >
              <MultiPicker
                values={values}
                config={c}
                expandAll={isSearching}
                onPick={(val) => {
                  updateFilter(c.filterKey, val);
                }}
                selected={filters?.[c.filterKey] as string[]}
              />
            </FilterSection>
          );
        }

        if (c.type === "RANGE") {
          const metadata = filtersMetadata?.find(
            (f) => f.field === c.fieldId
          ) as RangeFilterMetadata;

          const range: [number, number] = [
            c.formatter.in(metadata?.min ?? c?.min ?? 0),
            c.formatter.in(metadata?.max ?? c?.max ?? 100),
          ];

          if (filters[c.filterKey.min]) {
            range[0] = c.formatter.in(filters[c.filterKey.min] as string);
          }

          if (filters[c.filterKey.max]) {
            range[1] = c.formatter.in?.(filters[c.filterKey.max] as string);
          }

          return (
            <FilterSection
              open={isSearching ? true : undefined}
              defaultOpen={defaultOpen}
              key={c.id}
              title={c.label as string}
            >
              <RangePicker
                className="pt-4"
                min={c.formatter.in(metadata?.min ?? c?.min ?? 0)}
                max={c.formatter.in(metadata?.max ?? c?.max ?? 100)}
                step={c.step ?? 1}
                range={range}
                unit={c.formatter?.unit}
                onChange={([min, max]) => {
                  const change = {
                    [c.filterKey.min]: c.formatter.out(min)?.toString(),
                    [c.filterKey.max]: c.formatter.out(max)?.toString(),
                  };
                  onFiltersChange?.({
                    ...filters,
                    ...change,
                  });
                }}
              />
            </FilterSection>
          );
        }

        if (c.type === "TOGGLE") {
          return (
            <div
              key={c.id}
              className="h-12 flex flex-row items-center justify-between"
            >
              <span className="text-sm font-semibold">{c.label}</span>
              <Switch
                onCheckedChange={(checked) =>
                  onFiltersChange?.({
                    ...filters,
                    [c.filterKey]: checked ? "true" : "",
                  })
                }
                checked={filters[c.filterKey] === "true"}
              />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
